import { restManager } from '@/plugin/restManager';
import _ from 'lodash';

export const AuthorizationResult = {
  ALLOW: 'Allow',
  DENY: 'Deny',
  RESOURCE_NOT_FOUND: 'ResourceNotFound',
  ACCESS_DENIED: 'AccessDenied'
};

const defaultResult = {
  id: '',
  type: AuthorizationResult.ALLOW,
  serviceName: '',
  actionName: '',
  resourceIds: [],
  responseData: {},
  resourceNames: []
};

const authStore = {
  namespaced: true,
  state: () => ({
    authorizationCheckResult: []
  }),
  getters: {
    authorizationCheckResult: (state) => state.authorizationCheckResult
  },
  mutations: {
    setAuthorizationCheckResult: (state, result) => (state.authorizationCheckResult = result)
  },
  actions: {
    setAuthorizationCheckResult: ({ commit }, result) =>
      commit('setAuthorizationCheckResult', result),

    checkProductResource(
      { dispatch },
      {
        productTypeCode,
        actionName,
        productResourceIds,
        errorMessages = [],
        listDataCheckAuth = []
      }
    ) {
      return restManager.auth
        .checkProductResource(productTypeCode, actionName, productResourceIds, true)
        .then((data) =>
          dispatch('handleAuthorizationCheckResult', { data, errorMessages, listDataCheckAuth })
        )
        .catch((err) => err);
    },

    checkProductEventLevel(
      { dispatch },
      { productResourceId, productTypeCode, param, listDataCheckAuth = [] }
    ) {
      return restManager.event
        .get(
          `/v1/console/products/${productResourceId}/events`,
          param,
          true,
          false,
          {
            headers: { 'X-ResourceType': productTypeCode }
          },
          true
        )
        .then((data) => dispatch('handleAuthorizationCheckResult', { data, listDataCheckAuth }))
        .catch((err) => err);
    },

    checkAuthorizationCluster({ dispatch }, { params, productTypeCode, listDataCheckAuth = [] }) {
      return restManager.product
        .getObjectTypeList(params, productTypeCode, true)
        .then((data) => {
          return dispatch('handleAuthorizationCheckResult', { data, listDataCheckAuth });
        })
        .catch((err) => err);
    },

    checkProduct({ dispatch }, { params, loading = true, listDataCheckAuth = [] }) {
      return restManager.product
        .getProduct(params, loading, true)
        .then((data) => dispatch('handleAuthorizationCheckResult', { data, listDataCheckAuth }))
        .catch((err) => err);
    },

    checkProductDetail(
      { dispatch },
      { params, productTypeCode, listDataCheckAuth = [], loading = true }
    ) {
      return restManager.product
        .getProductDetail(params, productTypeCode, true, loading)
        .then((data) => {
          return dispatch('handleAuthorizationCheckResult', { data, listDataCheckAuth });
        })
        .catch((err) => err);
    },

    checkEventPolicies({ dispatch }, { policyId, productTypeCode, listDataCheckAuth = [] }) {
      return restManager.event
        .getEventPolicies(policyId, productTypeCode, true)
        .then((data) => dispatch('handleAuthorizationCheckResult', { data, listDataCheckAuth }))
        .catch((err) => err);
    },

    checkEventFilter({ dispatch }, { eventFilteringSq, listDataCheckAuth = [] }) {
      return new Promise((resolve, reject) => {
        dispatch(
          'eventState/detailEventFiltering',
          { eventFilteringSq: eventFilteringSq, needHandleAuthorizationCheck: true },
          { root: true }
        )
          .then((data) =>
            resolve(dispatch('handleAuthorizationCheckResult', { data, listDataCheckAuth }))
          )
          .catch((err) => reject(err));
      });
    },

    checkProductSubTypeAuthorization({ dispatch }, { productTypeCode }) {
      return restManager.product
        .checkProductSubTypeAuthorization(productTypeCode)
        .then((data) => dispatch('handleAuthorizationCheckResult', { data }))
        .catch((err) => err);
    },

    checkDeleteEventFilter({ dispatch }, { eventFilteringSq, listDataCheckAuth = [] }) {
      return new Promise((resolve, reject) => {
        dispatch(
          'eventState/deleteEventFiltering',
          {
            eventFilteringSq: eventFilteringSq,
            needHandleAuthorizationCheck: true
          },
          { root: true }
        )
          .then((data) =>
            resolve(dispatch('handleAuthorizationCheckResult', { data, listDataCheckAuth }))
          )
          .catch((err) => reject(err));
      });
    },

    removeResult({ dispatch, state }, resultId) {
      const result = state.authorizationCheckResult.filter((item) => item.id !== resultId);

      dispatch('setAuthorizationCheckResult', result);
    },

    addResult({ dispatch, state }, result) {
      const newResult = state.authorizationCheckResult.concat(result);
      dispatch('setAuthorizationCheckResult', newResult);
    },

    handleAuthorizationCheckResult(
      { dispatch },
      { data, errorMessages = [], listDataCheckAuth = [] }
    ) {
      let result = JSON.parse(JSON.stringify(defaultResult));
      result.id = Date.now().toString() + '-' + Math.random().toString();
      result.responseData = _.cloneDeep(data);

      if (
        Object.hasOwn(data, 'authorizationCheckResult') &&
        data.authorizationCheckResult === AuthorizationResult.DENY
      ) {
        result.type = AuthorizationResult.DENY;

        // Priority check resource not found first, If resource not found not exist, continue check denied resource
        const isNotFoundResourceId = checkResourceIdPermission(data.params, 'notFoundResourceId');
        const isDeniedResourceId = checkResourceIdPermission(data.params, 'deniedResourceId');
        if (isNotFoundResourceId) {
          result.resourceIds = extractResourceIdsFromParams(data.params, 'notFoundResourceId');
          result.type = AuthorizationResult.RESOURCE_NOT_FOUND;
        }
        if (!isNotFoundResourceId && isDeniedResourceId) {
          result.resourceIds = extractResourceIdsFromParams(data.params, 'deniedResourceId');
          result.type = AuthorizationResult.DENY;
        }
        //
        result.serviceName = data.params
          ? extractDataByKeyFromParams(data.params, 'service')
          : data.serviceName;
        result.actionName = data.params
          ? extractDataByActionsFromParams(data.params, 'action')
          : data.actionName;
      } else if (Object.hasOwn(data, 'code') && data.code === AuthorizationResult.ACCESS_DENIED) {
        result.type = AuthorizationResult.ACCESS_DENIED;
        result.resourceIds = extractResourceIdsFromParams(data.params);
        result.serviceName = data.params
          ? extractDataByKeyFromParams(data.params, 'service')
          : data.serviceName;
        result.actionName = data.params
          ? extractDataByActionsFromParams(data.params, 'action')
          : data.actionName;
      } else if (
        Object.hasOwn(data, 'code') &&
        data.code === AuthorizationResult.RESOURCE_NOT_FOUND
      ) {
        result.type = AuthorizationResult.RESOURCE_NOT_FOUND;
        result.resourceIds = extractResourceIdsFromParams(data.params);
      }
      result.errorMessages = errorMessages;
      // Function for checking resourceList same as resourceId
      const resourceList = Array.from(
        new Map(listDataCheckAuth.map((list) => [list.productResourceId, list])).values()
      );
      resourceList.forEach((e) => {
        if (result.resourceIds.includes(e.productResourceId))
          result.resourceNames.push(e.productName);
      });
      if (result.type !== AuthorizationResult.ALLOW) dispatch('addResult', result);
      return result;
    }
  }
};

const extractDataByKeyFromParams = (data, key) =>
  (Array.isArray(data) && data.find((item) => item.name === key)?.value) || '';

const extractDataByActionsFromParams = (data, key) =>
  (Array.isArray(data) && data.filter((item) => item.name === key).map((it) => it.value)) || [];

const extractResourceIdsFromParams = (data, name = 'resourceId') => {
  if (!data || !data.length) return [];
  return data.reduce((result, item) => {
    if (item.name === name) {
      result.push(...item.value);
    }
    return result;
  }, []);
};

const checkResourceIdPermission = (data, key) => {
  if (!data || !data.length || !key) return false;
  return data.some((dt) => dt.name === key && dt.value.length);
};

export default authStore;
